<script setup>
import { ref, onMounted, onUnmounted, watch } from "vue";
import IconsComp from "@/components/IconsComp";
import CompProgress from "@/components/CompProgress";
import {
    ContinuosFlowStep1View,
    ContinuosFlowStep2View,
    ContinuosFlowStep3View,
    ContinuosFlowStep4View,
    ContinuosFlowStep5View,
    ContinuosFlowStep6View,
} from "./continuos_flow_steps/index";
import { useProposalStore, useAuthStore, useDraftProposalStore, useAlertStore, useFormDownloadStore } from "@/stores";
import { Form, useForm } from "vee-validate";
import * as yup from "yup";
import { ptForm } from "yup-locale-pt";
import { schema, currentStep } from '@/schemas/continuosflowSteps'
import { useRouter, useRoute } from "vue-router";
import axios from "axios";

const route = useRoute();
const router = useRouter();
const formDownload = ref(null);
const isEditMode = ref(false);
const proposalId = ref(null);
const isSavingDraft = ref(false);

yup.setLocale(ptForm);

const steps = ref([
    "Introdução",
    "Equipe",
    "Estágio da solução",
    "Projeto",
    "Pitch",
]);

// Auto-save draft when changing steps (only for new proposals, not edits)
const autoSaveDraft = async () => {
    if (isEditMode.value || isSavingDraft.value) return;

    // Ensure edict is set for continuos-flow
    if (!values.edict) {
        values.edict = "continuos-flow";
    }

    isSavingDraft.value = true;
    try {
        await useDraftProposalStore().storeOrUpdate(values, true);
    } catch (error) {
        // Silently ignore errors during auto-save
        console.warn('Auto-save failed:', error);
    } finally {
        isSavingDraft.value = false;
    }
};

const nextStep = async () => {
    if (currentStep.value > 1 && currentStep.value < steps.value.length) {
        await autoSaveDraft();
        currentStep.value++;
        window.scroll({ top: 0, behavior: "smooth" });
    } else {
        currentStep.value++;
    }
};

const prevStep = async () => {
    if (currentStep.value > 1) {
        await autoSaveDraft();
        currentStep.value--;
    }
};

const { errors, values, handleSubmit, resetForm } = useForm({
    validationSchema: schema,
    keepValuesOnUnmount: true,
    keepValuesOnMount: true,
});

onMounted(async () => {
    formDownload.value = await useFormDownloadStore().all().then(response => response?.data?.find(obj => obj.nome == "Fluxo continuo"));

    // Check if editing existing proposal
    if (route.name === 'editar-proposta' && route.params.id) {
        isEditMode.value = true;
        proposalId.value = route.params.id;
    }

    const draftProposal = await useDraftProposalStore().findByEdict().then(response => response?.data);
    const savedData = JSON.parse(localStorage.getItem("form-continuos-flow"));

    if (!draftProposal && savedData !== null && !isEditMode.value) {
        localStorage.removeItem("form-continuos-flow");
    } else if (savedData && !isEditMode.value) {
        // Priority 1: restore from localStorage (most recent in-progress state)
        if (Array.isArray(savedData.specialty_areas)) {
            savedData.specialty_areas = savedData.specialty_areas[0] || null;
        }
        if (savedData.specialty_areas === "null" || savedData.specialty_areas === null) {
            delete savedData.specialty_areas;
        }
        resetForm({ values: savedData, force: true });
    } else if (draftProposal && !isEditMode.value) {
        // Priority 2: restore from backend draft when localStorage is empty
        const draftData = {
            project_name: draftProposal.nome_proposta,
            ...draftProposal.perguntas_e_respostas,
            specialty_areas: draftProposal.especialidades?.[0]?.id ?? draftProposal.especialidades?.[0],
        };
        resetForm({ values: draftData, force: true });
    }

    values["edict"] = "continuos-flow";
});

onUnmounted(() => {
    localStorage.removeItem("form-continuos-flow");
});
const saveDraftProposal = async () => {
    if (isEditMode.value) return;

    const alertStore = useAlertStore();
    alertStore.success("Salvando rascunho...");

    await useDraftProposalStore().storeOrUpdate(values).then((response) => {
        if (response) {
            alertStore.success(response.message || "Rascunho salvo com sucesso!");
            router.push({ name: 'painel-proponente' });
        }
    });
};

watch(errors, (newValue, oldValue) => {
    if (newValue != oldValue) {
        const firstErrorFieldName = Object.keys(errors.value)[0];
        const el = document.querySelector(`[name="${firstErrorFieldName}"]`);
        if (el) {
            el.scrollIntoView({
                behavior: 'auto',
                block: 'center',
                inline: 'center'
            });
        }
    }
});

// Final submission handler (with full validation)
const submitProposal = handleSubmit(async (formValues, { resetForm: formReset }) => {
    const alertStore = useAlertStore();

    try {
        let response;

        if (isEditMode.value && proposalId.value) {
            alertStore.success("Atualizando proposta...");
            response = await useProposalStore().updateData(proposalId.value, formValues);

            if (response?.proposta?.id || response?.data?.id) {
                const id = response?.proposta?.id || response?.data?.id;
                if (formValues['business_model']) {
                    await useProposalStore().storeFile(id, formValues);
                }
                if (formValues['pitch']) {
                    await useProposalStore().storeFile(id, formValues);
                }

                currentStep.value++;
                formReset();
                localStorage.removeItem("form-continuos-flow");
                alertStore.success(response?.message || 'Proposta atualizada com sucesso!');
            }
        } else {
            alertStore.success("Criando proposta...");
            response = await useProposalStore().store(formValues);

            if (response?.proposta?.id) {
                if (formValues['business_model']) {
                    await useProposalStore().storeFile(response.proposta.id, formValues);
                }
                if (formValues['pitch']) {
                    await useProposalStore().storeFile(response.proposta.id, formValues);
                }

                currentStep.value++;
                formReset();
                localStorage.removeItem("form-continuos-flow");
                alertStore.success(response?.message || 'Proposta criada com sucesso!');
            }
        }
    } catch (error) {
        if (error.response?.status === 422 && error.response?.data?.errors) {
            const errorMessages = Object.values(error.response.data.errors).flat();
            alertStore.error(errorMessages.join(', '));
        } else {
            alertStore.error(error.response?.data?.message || 'Ocorreu um erro ao salvar a proposta.');
        }
    }
});

// Handler for advancing to next step (validates but advances even with errors)
const onAdvance = handleSubmit(
    () => {
        // Validation passed - advance normally
        nextStep();
    },
    () => {
        // Validation failed - still advance but log warning
        console.warn('Validation errors on step', currentStep.value, '- advancing anyway');
        nextStep();
    }
);

// Form submit handler for final submission (strict validation)
const onSubmit = handleSubmit(
    async () => {
        // Validation passed - submit the proposal
        await submitProposal();
    },
    (validationErrors) => {
        // Validation failed - don't submit, show errors
        console.warn('Validation errors:', validationErrors);
    }
);
</script>
<template>
    <section v-if="currentStep > 0 && currentStep <= steps.length">
        <div class="container mx-auto px-6 pt-12 pb-10 md:w-8/12">
            <div class="mb-16">
                <CompProgress :currentStep="currentStep" :steps="steps" />
            </div>

            <div class="text-5xl font-bold font-poppins text-purple-800 text-center">
                Avaliação sob demanda de propostas de projetos
            </div>
        </div>
    </section>
    <section v-if="currentStep > 0 && currentStep <= steps.length" class="bg-purple-50 bg-opacity-80">
        <form @submit.prevent="onSubmit" keep-values>
            <div class="container mx-auto px-6 py-10 md:w-8/12">
                <ContinuosFlowStep1View v-if="currentStep === 1" :values="values">
                    <div class="flex mt-4 mb-16">
                        <button type="button" @click="nextStep" class="btn primary max-w-4">
                            Preencher formulário de incrição
                        </button>
                        <a :href="formDownload?.arquivo" download target="_blank" class="no-underline cursor-pointer">
                            <button type="button" class="btn secondary flex">
                                Baixar formulário
                                <IconsComp icon="download" class="fill-green-500 w-6 h-6 ml-4" />
                            </button>
                        </a>
                    </div>
                </ContinuosFlowStep1View>

                <ContinuosFlowStep2View v-if="currentStep === 2" :values="values" />

                <ContinuosFlowStep3View v-if="currentStep === 3" :values="values" />

                <ContinuosFlowStep4View v-if="currentStep === 4" :values="values" />

                <ContinuosFlowStep5View v-if="currentStep === 5" :values="values" />

                <div v-if="currentStep > 1 && currentStep <= steps.length">
                    <div class="flex my-8">
                        <button @click.prevent="prevStep" type="button" class="btn medium secondary">
                            Voltar
                        </button>
                        <button type="submit" v-if="currentStep === steps.length" class="flex btn medium primary">
                            {{ isEditMode ? 'Atualizar proposta' : 'Enviar proposta' }}
                            <IconsComp icon="arrow-left" class="ml-2 fill-white w-6 h-6" />
                        </button>
                        <button type="button" v-else @click="onAdvance" class="flex btn medium primary">
                            Avançar
                            <IconsComp icon="arrow-full-right" class="ml-2 fill-white w-6 h-6" />
                        </button>
                        <a v-if="!isEditMode" @click.prevent="saveDraftProposal"
                            class="flex items-center ml-auto no-underline font-bold cursor-pointer">
                            Salvar rascunho
                            <IconsComp icon="save" class="ml-2 fill-green-500 w-6 h-6" />
                        </a>
                    </div>
                    <p class="flex text-red-500">
                        <span class="asterisk mr-2"></span>
                        Campo obrigatório
                    </p>
                </div>
            </div>
        </form>
    </section>

    <section class="h-full">
        <ContinuosFlowStep6View v-if="currentStep === 6" />
    </section>
</template>
